import React, { useEffect, useState } from "react";
import axios, { AxiosError } from "axios";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import { getTargetYear } from "../components/SetGoalsBanner";

interface Goals {
  appointmentsHeldLastTwelveMonths: string;
  appointmentsSetLastTwelveMonths: string;
  avgCommission: string;
  avgSalePrice: string;
  buyerCommissionSplit: string;
  callsCanceledPerClosed: string;
  callsCirclePerClosed: string;
  callsFsboPerClosed: string;
  callsLastTwelveMonths: string;
  callsPerClosed: string;
  callsWebSignTeamPerClosed: string;
  contactsLastTwelveMonths: string;
  doorKnocksPerClosed: string;
  openHousesPerClosed: string;
  pendingToCloseRatio: string;
  sellerCommissionSplit: string;
  sellerToTotalClosingsRatio: string;
  signingsLastTwelveMonths: string;
  unitsClosedLastTwelveMonths: string;
  unitsPendedLastTwelveMonths: string;
  workingMonths?: string;
}

interface Errors {
  [key: string]: string;
}

interface Actions {
  saveGoals: () => Promise<boolean>;
  setGoals: React.Dispatch<React.SetStateAction<Goals>>;
}

interface UseProductionGoalsProps {
  show: boolean;
  userUuid?: string | null;
}

interface UseProductionGoalsReturn {
  actions: Actions;
  errors: Errors;
  goals: Goals;
  loading: boolean;
  year: number;
}

const EMPTY_GOALS: Goals = {
  appointmentsHeldLastTwelveMonths: "0",
  appointmentsSetLastTwelveMonths: "0",
  avgCommission: "0",
  avgSalePrice: "0",
  buyerCommissionSplit: "0",
  callsCanceledPerClosed: "0",
  callsCirclePerClosed: "0",
  callsFsboPerClosed: "0",
  callsLastTwelveMonths: "0",
  callsPerClosed: "0",
  callsWebSignTeamPerClosed: "0",
  contactsLastTwelveMonths: "0",
  doorKnocksPerClosed: "0",
  openHousesPerClosed: "0",
  pendingToCloseRatio: "0",
  sellerCommissionSplit: "0",
  sellerToTotalClosingsRatio: "0",
  signingsLastTwelveMonths: "0",
  unitsClosedLastTwelveMonths: "0",
  unitsPendedLastTwelveMonths: "0",
  workingMonths: "0",
};

const cleanValues = (goals: Goals): Goals =>
  Object.entries(goals).reduce((acc, [key, value]) => {
    acc[key as keyof Goals] = String(value).replace(/[^0-9.]/g, "");
    return acc;
  }, {} as Goals);

const transformGoals = (goals: Record<string, string | number | null>): Goals =>
  Object.entries(goals).reduce((acc, [key, value]) => {
    if (["id", "accountId", "createdAt", "updatedAt"].includes(key)) return acc;
    acc[key as keyof Goals] = typeof value === "number" ? value.toLocaleString() : String(value ?? 0);
    return acc;
  }, {} as Goals);

const validateGoals = (goals: Goals, goalWizard2: boolean): Errors =>
  Object.entries(goals)
    .filter(
      ([key]) =>
        goalWizard2 ||
        key.match(
          /avgSalePrice|avgCommission|sellerCommissionSplit|buyerCommissionSplit|pendingToCloseRatio|sellerToTotalClosingsRatio/,
        ),
    )
    .reduce((acc, [key, value]) => {
      const cleanValue = String(value).replace(/[^0-9.]/g, "");
      if (cleanValue.length === 0) acc[key] = "Required field";
      else if (!parseFloat(cleanValue)) acc[key] = "Value must be greater than 0";
      return acc;
    }, {} as Errors);

const getGoals = (...config: object[]): Promise<Goals> =>
  caseTransformingAxios
    .get("/api/v4/goals/show_wizard_team_defaults", ...config)
    .then((res) => transformGoals(res.data as Record<string, string | number | null>));

const upsertGoals = ({ workingMonths, ...goals }: Goals, ...config: object[]): Promise<Goals> =>
  caseTransformingAxios
    .post(
      "/api/v4/goals/upsert_wizard_team_defaults",
      { userGoalsSetting: { workingMonths }, goalWizardTeamDefaults: cleanValues(goals) },
      ...config,
    )
    .then((res) => transformGoals(res.data as Record<string, string | number | null>));

const useProductionGoals = ({ show, userUuid }: UseProductionGoalsProps): UseProductionGoalsReturn => {
  const featureFlags = useFeatureFlags() as { goalWizard2: boolean };
  const [goals, setGoals] = useState<Goals>({ ...EMPTY_GOALS });
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      const abortController = new AbortController();
      setLoading(true);
      setErrors({});
      getGoals({ signal: abortController.signal, params: { userUuid } })
        .then((data) => {
          setLoading(false);
          setGoals(data);
        })
        .catch((err: AxiosError) => {
          if (!axios.isCancel(err)) {
            setLoading(false);
            if (err.response?.status === 404) return;
            setErrors({ global: "Failed to load Goals" });
          }
        });
      return () => {
        abortController.abort();
      };
    }

    setErrors({});
    return () => {};
  }, [show, userUuid]);

  return {
    actions: {
      saveGoals: () => {
        const newErrors = validateGoals(goals, featureFlags.goalWizard2);
        setErrors(newErrors);
        if (!Object.keys(newErrors).length) {
          setLoading(true);
          return upsertGoals(goals, { params: { userUuid } })
            .then((data) => {
              setLoading(false);
              setGoals(data);
              return true;
            })
            .catch(() => {
              setLoading(false);
              setErrors({ global: "Failed to save Goals, try again later." });
              return false;
            });
        }
        return Promise.resolve(false);
      },
      setGoals,
    },
    errors,
    goals,
    loading,
    year: getTargetYear(),
  };
};

export default useProductionGoals;
